import { ScrollArea } from "@mantine/core";
import IconSparkles from "@src/assets/icons/lucide/sparkles.svg?react";
import IconBrandWhatsapp from "@src/assets/icons/tabler/brand-whatsapp.svg?react";
import IconPencilMinus from "@src/assets/icons/tabler/pencil.svg?react";
import IconStackFront from "@src/assets/icons/tabler/stack-front.svg?react";
import { ROUTES } from "@src/common/constants";
import { useOptionalAgentChatContext } from "@src/components/agent-chat/hooks/agent-chat-context";
import { WhatsAppIntegrationModalButton } from "@src/components/modals/whatsapp-integration-modal/whatsapp-integration-modal";
import { FavouritesPanel } from "@src/components/navigation/nav-section-tree/favourites-panel";
import { NavTreeCreateBoardRow } from "@src/components/navigation/nav-section-tree/nav-create-whiteboard-button";
import {
	NavSectionTabs,
	type NavTreeTab,
} from "@src/components/navigation/nav-section-tree/nav-section-tabs";
import { NavTreePanel } from "@src/components/navigation/nav-section-tree/nav-tree-panel";
import { SharedTreePanel } from "@src/components/navigation/nav-section-tree/shared-tree-panel";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import { NavbarSection } from "@src/gql/graphql";
import clsx from "clsx";
import { useCallback, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { NavLink, useLocation } from "react-router-dom";
import { MentionInboxBell } from "../mentions/inbox/mention-inbox-bell";
import { useAuthenticatedUser } from "../user/user-context";
import { NavActiveSourceProvider } from "./context/nav-active-source-context";
import { NavBarHandlerProvider } from "./context/nav-bar-handler-context";
import { NavPendingRenameProvider } from "./context/nav-pending-rename-context";
import { SearchProvider } from "./context/search-context";
import { NavBarFooter } from "./nav-bar-footer";
import { NavBarSearchComponent, useShowNavBarSearch } from "./nav-bar-search";
import NavSectionErrorItem from "./nav-section-panel/nav-section-error-item";
import { UserButton } from "./user-button";

const ACTIVE_TAB_STORAGE_KEY = "nav-tree-active-tab";

function getInitialNavTab(): NavTreeTab {
	if (typeof window === "undefined") return "workspace";
	try {
		const stored = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
		if (stored === "workspace" || stored === "shared" || stored === "pinned") {
			return stored;
		}
	} catch {
		// Ignore storage read error
	}
	return "workspace";
}

function sectionForTab(tab: NavTreeTab): NavbarSection {
	return tab === "shared"
		? NavbarSection.SharedWithMe
		: NavbarSection.MyWhiteboards;
}

function NavBarContent({ activeTab }: { activeTab: NavTreeTab }) {
	return (
		<div className="px-3">
			{activeTab === "pinned" && (
				<ErrorBoundary
					fallback={<NavSectionErrorItem label="Failed to load pinned items" />}
				>
					<FavouritesPanel hideSectionHeader />
				</ErrorBoundary>
			)}

			{activeTab === "workspace" && (
				<ErrorBoundary
					fallback={<NavSectionErrorItem label="Failed to load boards" />}
				>
					<NavTreePanel hideSectionHeader />
				</ErrorBoundary>
			)}

			{activeTab === "shared" && (
				<ErrorBoundary
					fallback={
						<NavSectionErrorItem label="Failed to load shared boards" />
					}
				>
					<SharedTreePanel hideSectionHeader />
				</ErrorBoundary>
			)}
		</div>
	);
}

function NavSearchCreateRow({
	activeTab,
	isAskActive,
	onAskClick,
}: {
	activeTab: NavTreeTab;
	isAskActive: boolean;
	onAskClick: () => void;
}) {
	const showSearch = useShowNavBarSearch();

	return (
		<div className="flex flex-col gap-1 px-3">
			<div
				className={clsx(
					"grid h-8 w-full grid-cols-2 overflow-hidden rounded-lg",
					"border border-brand-primary/30 bg-brand-primary/[0.06]",
				)}
			>
				{/* min-w-0 on the grid cell so Tooltip/content cannot blow
				    past 50% (default grid min-width: auto). */}
				<div className="h-full min-w-0 w-full">
					<Tooltip label="Ask Illumi" openDelay={300} withArrow>
						<NavLink
							to={ROUTES.ask}
							end
							aria-label="Ask Illumi"
							aria-current={isAskActive ? "page" : undefined}
							onClick={onAskClick}
							className={clsx(
								"flex h-full min-w-0 w-full items-center justify-center",
								"gap-1.5 px-2.5 text-xs font-semibold",
								"select-none",
								isAskActive
									? "bg-brand-primary text-text-inverse"
									: [
											"border-r border-brand-primary/30",
											"bg-transparent text-brand-primary",
											"hover:bg-brand-primary/12",
										],
							)}
						>
							<IconSparkles
								width={14}
								height={14}
								className={clsx(
									"shrink-0",
									isAskActive ? "text-text-inverse" : "text-brand-primary",
								)}
							/>
							<span className="truncate leading-normal">Ask Illumi</span>
						</NavLink>
					</Tooltip>
				</div>
				<ErrorBoundary
					fallback={
						<NavSectionErrorItem label="Failed to load board actions" />
					}
				>
					<div className="h-full min-w-0 w-full">
						<NavTreeCreateBoardRow
							compact={false}
							merged
							section={sectionForTab(activeTab)}
						/>
					</div>
				</ErrorBoundary>
			</div>
			{showSearch ? (
				<div className="w-full min-w-0">
					<NavBarSearchComponent />
				</div>
			) : null}
		</div>
	);
}

interface NavBarProps {
	innerScroll?: boolean;
}

export function NavBar({ innerScroll = true }: NavBarProps) {
	const authenticatedUser = useAuthenticatedUser();
	const agentChat = useOptionalAgentChatContext();
	const location = useLocation();
	const [activeTab, setActiveTab] = useState<NavTreeTab>(getInitialNavTab);

	const handleTabChange = useCallback((tab: NavTreeTab) => {
		setActiveTab(tab);
		try {
			localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, tab);
		} catch {
			// Ignore storage write error
		}
	}, []);

	const handleAskClick = useCallback(() => {
		agentChat?.startNewChat();
	}, [agentChat]);

	const isWhatsAppConnected = Boolean(authenticatedUser.phoneNumber);
	const isDraftActive = location.pathname.startsWith(ROUTES.draft);
	const isLibraryActive = location.pathname.startsWith(ROUTES.library);
	const isAskActive = location.pathname === ROUTES.ask;

	return (
		<nav
			className={clsx(
				"flex h-full min-h-0 flex-col gap-1",
				"overflow-y-auto bg-surface-default",
			)}
		>
			<div className="flex flex-col gap-1 px-3 pt-1.5">
				<UserButton
					className="p-1"
					rightSlot={
						<MentionInboxBell
							size="sm"
							variant="navbar"
							className={clsx(
								"h-7 w-7 rounded-md border border-transparent",
								"bg-transparent hover:bg-surface-hover-subtle",
							)}
						/>
					}
				/>
			</div>

			<SearchProvider>
				<NavActiveSourceProvider>
					<NavBarHandlerProvider>
						<NavPendingRenameProvider>
							<div className="flex min-h-0 flex-1 flex-col gap-1">
								<NavSearchCreateRow
									activeTab={activeTab}
									isAskActive={isAskActive}
									onAskClick={handleAskClick}
								/>

								<div className="flex flex-col gap-1 px-3">
									<div className="relative h-7.5 w-full">
										<Tooltip
											label="Draft notes and scratchpad"
											openDelay={300}
											withArrow
										>
											<NavLink
												to={ROUTES.draft}
												aria-label="Draft notes and scratchpad"
												className={clsx(
													"absolute inset-0 flex flex-row flex-nowrap items-center",
													"gap-2.5 w-full h-7.5 pl-2.5 pr-8 rounded-lg text-xs",
													"font-medium transition-colors select-none",
													isDraftActive
														? "bg-interaction-selected font-semibold text-brand-primary"
														: "text-text-primary hover:bg-surface-hover-subtle",
												)}
											>
												<IconPencilMinus
													width={15}
													height={15}
													className="shrink-0"
												/>
												<span className="truncate leading-normal">Draft</span>
											</NavLink>
										</Tooltip>
										<div
											className={clsx(
												"pointer-events-none absolute inset-y-0 right-1.5",
												"flex items-center",
											)}
										>
											{isWhatsAppConnected ? (
												<Tooltip
													label="WhatsApp connected"
													openDelay={300}
													withArrow
												>
													<span
														aria-label="WhatsApp connected"
														className={clsx(
															"pointer-events-auto h-2 w-2 shrink-0",
															"rounded-full bg-whatsapp-primary",
														)}
													/>
												</Tooltip>
											) : (
												<WhatsAppIntegrationModalButton source="navbar">
													<Tooltip
														label="Connect WhatsApp"
														openDelay={300}
														withArrow
													>
														<button
															type="button"
															title="Connect WhatsApp"
															aria-label="Connect WhatsApp"
															className={clsx(
																"flex h-5.5 w-5.5 shrink-0 cursor-pointer items-center",
																"justify-center rounded-md text-whatsapp-primary",
																"pointer-events-auto transition-colors",
																"hover:bg-whatsapp-primary/15",
															)}
														>
															<IconBrandWhatsapp
																width={14}
																height={14}
																className="shrink-0"
															/>
														</button>
													</Tooltip>
												</WhatsAppIntegrationModalButton>
											)}
										</div>
									</div>

									<div className="relative h-7.5 w-full">
										<Tooltip label="Card Library" openDelay={300} withArrow>
											<NavLink
												to={ROUTES.library}
												aria-label="Card Library"
												className={clsx(
													"absolute inset-0 flex flex-row flex-nowrap items-center",
													"gap-2.5 w-full h-7.5 pl-2.5 pr-3 rounded-lg text-xs",
													"font-medium transition-colors select-none",
													isLibraryActive
														? "bg-interaction-selected font-semibold text-brand-primary"
														: "text-text-primary hover:bg-surface-hover-subtle",
												)}
											>
												<IconStackFront
													width={15}
													height={15}
													className="shrink-0"
												/>
												<span className="truncate leading-normal">Library</span>
											</NavLink>
										</Tooltip>
									</div>
								</div>

								<NavSectionTabs
									activeTab={activeTab}
									onTabChange={handleTabChange}
								/>
								{innerScroll ? (
									<ScrollArea
										scrollbarSize={4}
										type="scroll"
										className="min-h-0 flex-1"
									>
										<NavBarContent activeTab={activeTab} />
									</ScrollArea>
								) : (
									<div className="min-h-0 flex-1 overflow-y-auto">
										<NavBarContent activeTab={activeTab} />
									</div>
								)}
							</div>
						</NavPendingRenameProvider>
					</NavBarHandlerProvider>
				</NavActiveSourceProvider>
			</SearchProvider>

			<NavBarFooter />
		</nav>
	);
}
